/**
 * The brand as every non-editor surface receives it, and the public handle
 * rules. Zod-free, so any app can import it alone
 * ('@tendedloop/shared/brand-public').
 *
 * The server resolves a tenant's stored brand into a `ResolvedBrand` (see
 * resolveBrand in packages/api), and only for a tenant whose brand identity
 * TendedLoop verified. Clients never resolve paths or storage keys: every URL
 * here is absolute. Null anywhere means "use TendedLoop's own look".
 */
/** The allowlist, applied field by field (never a spread). */
export function toPublicBrand(b) {
    return {
        displayName: b.displayName,
        logoOnDarkUrl: b.logoOnDarkUrl,
        logoOnLightUrl: b.logoOnLightUrl,
        partnerLogoOnDarkUrl: b.partnerLogoOnDarkUrl,
        splashUrl: b.splashUrl,
        logoBackground: b.logoBackground,
        primary: b.primary,
        done: b.done,
        highlight: b.highlight,
        socialImageUrl: b.socialImageUrl ?? null,
    };
}
// ---------------------------------------------------------------------------
// Public handles
// ---------------------------------------------------------------------------
/** 3 to 40 characters: lowercase letters, digits and inner hyphens. */
export const PUBLIC_HANDLE_RE = /^[a-z0-9](?:[a-z0-9-]{1,38}[a-z0-9])$/;
/**
 * Names no tenant may take: TendedLoop's own, the app's routes and
 * addresses, and their Hebrew transliterations (residents type what they hear).
 */
export const RESERVED_PUBLIC_HANDLES = new Set([
    // TendedLoop itself
    'tendedloop',
    'tended-loop',
    'tendedlup',
    'tended-lup',
    'tendedlop',
    'tendlup',
    'tl',
    // App routes, hosts and roles
    'o',
    'www',
    'app',
    'apps',
    'api',
    'admin',
    'administrator',
    'superadmin',
    'super-admin',
    'root',
    'system',
    'staff',
    'office',
    'dashboard',
    'login',
    'logout',
    'signin',
    'sign-in',
    'signup',
    'sign-up',
    'register',
    'auth',
    'oauth',
    'sso',
    'account',
    'accounts',
    'settings',
    'billing',
    'support',
    'help',
    'helpdesk',
    'security',
    'status',
    'docs',
    'residents',
    'resident',
    'portal',
    'visitor',
    'scout',
    'brand',
    'brands',
    'public',
    'static',
    'assets',
    'cdn',
    'mail',
    'email',
    'noreply',
    'no-reply',
    'postmaster',
    'webmaster',
    'abuse',
    'privacy',
    'terms',
    'legal',
    'demo',
    'try',
    'test',
    'null',
    'undefined',
    // Hebrew transliterations: residents (דיירים), housing (דיור), the portal,
    // sign-in (כניסה), support (תמיכה), help (עזרה), security (אבטחה), office (משרד)
    'dayarim',
    'dairim',
    'dayyarim',
    'portal-dayarim',
    'portal-dairim',
    'diyur',
    'diur',
    'knisa',
    'tmicha',
    'tmikha',
    'ezra',
    'avtacha',
    'avtaha',
    'misrad',
]);
/** A handle a tenant may hold: the right shape, not reserved, never naming TendedLoop. */
export function isAllowedPublicHandle(handle) {
    if (!PUBLIC_HANDLE_RE.test(handle))
        return false;
    if (RESERVED_PUBLIC_HANDLES.has(handle))
        return false;
    const squashed = handle.replace(/-/g, '');
    return !/tendedl(?:oo|u|o)p/.test(squashed);
}
//# sourceMappingURL=brand-public.js.map